import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { BrowserRouter, useLocation } from 'react-router-dom';
import App from './App';
import {
  checkFieldMapCacheOwner,
  restoreFieldMapQueries,
  setupFieldMapPersistence,
} from './hooks/useOnlineStatus';
import './styles/index.css';
import { installPreloadErrorReload } from './utils/chunkReload';

// Brief 2026-10-30-006: a chunk preload that fails after a deploy reloads the page once.
installPreloadErrorReload();

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// V17.520 (brief 2026-10-25-004): the field map's last-loaded portfolio survives
// a reload with no signal. Only the field-map queries, only for the logged-in
// user, at most 3 days old (see hooks/useOnlineStatus.ts).
restoreFieldMapQueries(queryClient);
setupFieldMapPersistence(queryClient);

/** Logout clears the token and navigates: a new owner on any route change drops the old user's cached data. */
function FieldMapCacheOwnerGuard() {
  const location = useLocation();
  useEffect(() => {
    void checkFieldMapCacheOwner(queryClient);
  }, [location.key]);
  // Another tab logged out or in as someone else: same check.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'access_token' || e.key === null) void checkFieldMapCacheOwner(queryClient);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  return null;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <FieldMapCacheOwnerGuard />
        <App />
      </BrowserRouter>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </React.StrictMode>
);
// Cache bust 1765601034
